import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DELIVERY_METHODS } from './mapper-library.model';
import { Basket, DeliveryGroupInfo, DeliveryInfo, Product, TransactionPromotion } from './mapper-library.model';
import { TransactionDiscount } from './mapper-library.model';
import { NumberUtilService } from './number-util.service';
import {AppLoggerService} from '@pmcretail/common-services/common/app-logger.service';

export class BasketService {
  currentBasket: Basket = {
    products: [],
    transactionDiscount: [],
    quantity: 0,
    total: 0,
    saleTotal: 0,
    baseCurrency: ''
  };

  private basketSource = new BehaviorSubject<any>(null);
  basket = this.basketSource.asObservable();

  private productSource = new BehaviorSubject<any>(null);
  product = this.productSource.asObservable();

  private peerSource = new BehaviorSubject<boolean>(false);
  peerSync = this.peerSource.asObservable();

  private scanSource = new BehaviorSubject<boolean>(false);
  scanner = this.scanSource.asObservable();

  private _isMixedBasketSales: boolean | undefined;
  public get isMixedBasketSales(): any {
    return this._isMixedBasketSales;
  }
  public set isMixedBasketSales(v: any) {
    this._isMixedBasketSales = v;
  }
  className = "BasketService";

  constructor(private numberUtilService: NumberUtilService) { }

  updateBasket(basket: Basket) {
    this.currentBasket = basket;
    this.isMixedBasketSales = this.filterProductListWithWebOrder(true).length > 0;
    this.basketSource.next(basket);
  }

  /**
   * calculates the total basket value and quantity
   * @param  null
   * @returns void
   */
  updateBasketTotal(basketDetails: { quantity: number; total: number; products: any; transactionId: any; basketTotal: any; }, basketState: { updateBasket: (arg0: any) => void; }) {
    basketDetails.quantity = 0;
    basketDetails.total = 0;
    for (const p of basketDetails.products) {
      basketDetails.quantity = basketDetails.quantity + p.quantity;
      basketDetails.total =
        this.numberUtilService.trimTo2Decimal(basketDetails.total) +
        this.numberUtilService.trimTo2Decimal(p.total);
    }
    AppLoggerService.logDebug(this.className, "updateBasketTotal",
      `basketDetails updated values for transaction id
      ${basketDetails.transactionId},  basketDetails
      ${JSON.stringify(basketDetails)}`
    );
    basketDetails.basketTotal = basketDetails.total;
    basketState.updateBasket(basketDetails);
  }

  /**
   * add Line promotion
   * @param  null
   * @returns basketDetails
   */
  addlinePromotion(promotionList: { result: { linePromotions: string | any[]; }; }, basketDetails: { products: any; }) {
    AppLoggerService.logDebug(this.className, "addlinePromotion",
      `promotionList ${JSON.stringify(promotionList)} and 
       basketDetails
      ${JSON.stringify(basketDetails)} for linePromotions`
    );
    if (
      promotionList &&
      promotionList.result &&
      promotionList.result.linePromotions &&
      promotionList.result.linePromotions.length > 0
    ) {
      for (var element of basketDetails.products) {
        for (var promotionelement of promotionList.result.linePromotions) {
          if (
            element.itemLineId.toString() ===
            promotionelement.itemLineId.toString()
          ) {
            element.linePromotions = promotionelement;
          }
        }
      }
    }
    return basketDetails;
  }

  /**
   * add Transaction promotion
   * @param  null
   * @returns basketDetails
   */

  addTransactionPromotion(promotionList: { result: { transactionPromotion: any }; }, basketdetails: Basket) {
    AppLoggerService.logDebug(this.className, "addTransactionPromotion",
      `promotionList ${JSON.stringify(promotionList)} and 
       basketDetails
      ${JSON.stringify(basketdetails)} for transactionPromotions ${JSON.stringify(basketdetails.transactionPromotion)}`
    );
    if (
      promotionList &&
      promotionList.result &&
      promotionList.result.transactionPromotion &&
      promotionList.result.transactionPromotion.length > 0
    ) {
      promotionList.result.transactionPromotion.forEach((tPromotion: any) => {
        let transactionPromotionEntryUpdated: any = Object.assign(
          {},
          tPromotion
        );

        transactionPromotionEntryUpdated.itemLineIds = basketdetails.products.map(
          (product) => {
            return product.itemLineId;
          }
        );

        if (!basketdetails.transactionPromotion) {
          basketdetails.transactionPromotion = [];
        }
        basketdetails.transactionPromotion.push(
          transactionPromotionEntryUpdated
        );

        basketdetails = this.transactionPromotionProductPortionCalc(
          basketdetails,
          transactionPromotionEntryUpdated
        );
      });
    }
    AppLoggerService.logDebug(this.className, "addTransactionPromotion",
      "basket details in refund calculation file",
      basketdetails
    );
    return basketdetails;
  }

  updateProductTotalForSummary(basketDetails: { total: string | number; quantity: string | number; products: any; }) {
    /*sale-            product refundedQuantity = undefined
    partial refund -   product refundedQuantity is > 0
    total refund -     proudct refundedQuantity is > 0
    exchange -         exchange product refundedQuantity is > 0  
                       new product refundedQuantity = undefined  
                       old product without exchange refundedQuantity = 0
    unreference refund-product refundedQuantity is > 0 */

    basketDetails.total = 0;
    basketDetails.quantity = 0;
    for (var product of basketDetails.products) {
      if (product.linePromotions) {
        product.total = product.linePromotions.promotionLinePrice;
      } else if (product.lineDiscount && !product.linePromotions) {
        product.total = product.lineDiscount.discountedLinePrice;
      } else {
        product.total = product.totalLinePrice;
      }
      product.total =
        product.total -
        Math.abs(product.refundedTotal ? product.refundedTotal : 0);

      basketDetails.total += product.total;
      basketDetails.quantity =
        basketDetails.quantity +
        (product.quantity -
          (product.refundedQuantity ? product.refundedQuantity : 0));
      AppLoggerService.logDebug(this.className, "updateProductTotalForSummary",
        "update basketdetails total :" +
        basketDetails.total +
        " Product line ID : " +
        product.itemLineId +
        " product total : " +
        product.total,
        "quantity  : " + basketDetails.quantity
      );
    }
    return this.updateBasketTotalForSummary(basketDetails);
  }

  updateBasketTotalForSummary(basketDetails: any) {
    basketDetails.basketTotal = basketDetails.total;
    AppLoggerService.logInfo(this.className, "updateBasketTotalForSummary",
      "update Basket Total for transaction transactionDiscount  ",
      basketDetails.transactionDiscount
    );

    AppLoggerService.logInfo(this.className, "updateBasketTotalForSummary",
      "update Basket Total for transaction transactionPromotion  ",
      basketDetails.transactionPromotion
    );

    basketDetails = this.updateTransactionPromotion(basketDetails);
    basketDetails = this.updateTransactionDiscount(basketDetails);
    basketDetails.basketTotal = Number(basketDetails.basketTotal.toFixed(2));

    AppLoggerService.logInfo(this.className, "updateBasketTotalForSummary",
      "update Basket Total: basketTotal after promotion applied  for basketTotal",
      basketDetails.basketTotal
    );
    return basketDetails;
  }

  updateTransactionDiscount(basketDetails: { transactionDiscount: any[]; basketTotal: number; }) {
    if (
      basketDetails.transactionDiscount &&
      basketDetails.transactionDiscount.length > 0
    ) {
      const discountTotal = basketDetails.transactionDiscount.reduce(
        (n: any, { discountAmount }: any) => n + discountAmount,
        0
      );
      AppLoggerService.logDebug(this.className, "updateTransactionDiscount",  "discountTotal ", discountTotal);
      basketDetails.basketTotal =
        basketDetails.basketTotal - Math.abs(discountTotal);
    }
    return basketDetails;
  }

  updateTransactionPromotion(basketDetails: { transactionPromotion: any[]; basketTotal: number; }) {
    if (
      basketDetails.transactionPromotion &&
      basketDetails.transactionPromotion.length > 0
    ) {
      const discountTotal = basketDetails.transactionPromotion.reduce(
        (n: any, { promotionAmount }: any) => n + promotionAmount,
        0
      );
      basketDetails.basketTotal =
        basketDetails.basketTotal - Math.abs(discountTotal);
    }
    return basketDetails;
  }

  updateBasketTotalWithoutDiscount(basket: Basket): Basket {
    basket.total = 0;
    basket.quantity = 0;

    for (var product of basket.products) {
      if (product.linePromotions) {
        product.total = product.linePromotions.promotionLinePrice;
      } else if (product.lineDiscount && !product.linePromotions) {
        product.total = product.lineDiscount.discountedLinePrice || 0;
      } else {
        product.total = product.totalLinePrice;
      }

      product.total =
        product.total -
        Math.abs(product.refundedTotal ? product.refundedTotal : 0);
      basket.total += product.total;
      basket.quantity =
        basket.quantity +
        (product.quantity -
          (product.refundedQuantity ? product.refundedQuantity : 0));

      AppLoggerService.logDebug(this.className, "updateBasketTotalWithoutDiscount",
        "e-basket:basket service:update basketdetails total :" +
        JSON.stringify(basket) +
        basket.total +
        " Product line ID : " +
        product.itemLineId +
        " product total : " +
        product.total
      );
    }

    basket.basketTotal = basket.total;
    return basket;
  }

  updateBasketTotalWithDiscounts(basket: any): Basket {
    AppLoggerService.logDebug(this.className, "updateBasketTotalWithDiscounts",
      `this.basketDetails new values  ${basket}, 
      ${JSON.stringify(basket.basketTotal)}`
    );
    if (basket.transactionPromotion && basket.transactionPromotion.length > 0) {
      const discountTotal = basket.transactionPromotion.reduce(
        (n: any, { promotionAmount }: any) => n + promotionAmount,
        0
      );
      basket.basketTotal = basket.basketTotal - discountTotal;
    }
    if (basket.transactionDiscount && basket.transactionDiscount.length > 0) {
      const discountTotal = basket.transactionDiscount.reduce(
        (n: any, { discountAmount }: any) => n + discountAmount,
        0
      );

      basket.basketTotal = basket.basketTotal - discountTotal;
    }
    AppLoggerService.logDebug(this.className, "updateBasketTotalWithDiscounts",
      "basket new values",
      basket
    );

    return basket;
  }

  transactionDiscountAndPromotionProductortionCalculation(basket: Basket) {
    if (basket.transactionDiscount && basket.transactionDiscount.length > 0) {
      basket.transactionDiscount.forEach(
        (discount) =>
        (basket = this.transactionDiscountProductPortionCalc(
          basket,
          discount
        ))
      );
    }

    if (basket.transactionPromotion && basket.transactionPromotion.length > 0) {
      basket.transactionPromotion.forEach((promotion) => {
        basket = this.transactionPromotionProductPortionCalc(basket, promotion);
      });
    }
    AppLoggerService.logDebug(this.className, "transactionDiscountAndPromotionProductortionCalculation",
      `basket item discount protion calculation ,
      ${basket},
      ${basket.products}`
    );
    return basket;
  }

  transactionPromotionProductPortionCalc(
    basket: any,
    promotion: any
  ) {
    let total = 0;
    let percentage = 0;
    {
      basket.products.forEach((p:any) => {
        if (promotion.itemLineIds.includes(p.itemLineId)) {
          p = this.validateProductItemDiscount(p);
          const productContributionInTotal =
            this.numberUtilService.trimTo2Decimal2(p.itemDiscounts.trnDiscountsPortion) +
            this.numberUtilService.trimTo2Decimal2(p.itemDiscounts.trnPromotionsPortion) -
            this.numberUtilService.trimTo2Decimal2(p.itemDiscounts.refundPortion);
          total += (p.total - this.numberUtilService.trimTo2Decimal2(productContributionInTotal));
        }
      });
      AppLoggerService.logDebug(this.className, "transactionPromotionProductPortionCalc",
        `ebasket: trnsactionPromotion: ${JSON.stringify(
          promotion
        )}, selected products total: ${total}`
      );
      basket.products.forEach((product: any) => {
        product = this.validateProductItemDiscount(product);

        const productContributionInTotal =
          this.numberUtilService.trimTo2Decimal2(product.itemDiscounts.trnDiscountsPortion) +
          this.numberUtilService.trimTo2Decimal2(product.itemDiscounts.trnPromotionsPortion) -
          this.numberUtilService.trimTo2Decimal2(product.itemDiscounts.refundPortion);

        if (promotion.itemLineIds.includes(product.itemLineId)) {
          percentage = ((product.total - this.numberUtilService.trimTo2Decimal2(productContributionInTotal)) / total) * 100;
          product.itemDiscounts.trnPromotionsPortion +=
            (promotion.promotionAmount * percentage) / 100;
          product.itemDiscounts.trnPromotionsPortion = this.numberUtilService.trimTo2Decimal2(product.itemDiscounts.trnPromotionsPortion);
        }
      });
    }
    return basket;
  }

  transactionDiscountProductPortionCalc(
    basket: any,
    discount: any
  ) {
    let total = 0;
    let percentage = 0;

    basket.products.forEach((p: any) => {
      if (discount.itemLineIds.includes(p.itemLineId)) {
        p = this.validateProductItemDiscount(p);
        const productContributionInTotal =
          this.numberUtilService.trimTo2Decimal2(p.itemDiscounts.trnDiscountsPortion) +
          this.numberUtilService.trimTo2Decimal2(p.itemDiscounts.trnPromotionsPortion) -
          this.numberUtilService.trimTo2Decimal2(p.itemDiscounts.refundPortion);


        total += (p.total - this.numberUtilService.trimTo2Decimal2(productContributionInTotal));
      }
    });

    AppLoggerService.logDebug(this.className, "transactionDiscountProductPortionCalc",
      `ebasket: trnsactionDiscount: ${JSON.stringify(
        discount
      )}, selected products total: ${total}`
    );

    basket.products.forEach((product:any) => {
      product = this.validateProductItemDiscount(product);
      AppLoggerService.logDebug(this.className, "transactionDiscountProductPortionCalc", "discountable in basket service", product.discountable);

      const productContributionInTotal =
        this.numberUtilService.trimTo2Decimal2(product.itemDiscounts.trnDiscountsPortion) +
        this.numberUtilService.trimTo2Decimal2(product.itemDiscounts.trnPromotionsPortion) -
        this.numberUtilService.trimTo2Decimal2(product.itemDiscounts.refundPortion);

      if (discount.itemLineIds.includes(product.itemLineId) && product.discountable == true) {
        percentage = ((product.total - this.numberUtilService.trimTo2Decimal2(productContributionInTotal)) / total) * 100;
        product.itemDiscounts.trnDiscountsPortion +=
          (discount.discountAmount * percentage) / 100;
        product.itemDiscounts.trnDiscountsPortion = this.numberUtilService.trimTo2Decimal2(product.itemDiscounts.trnDiscountsPortion)
      }
    });
    return basket;
  }

  public validateProductItemDiscount(product: Product) {
    if (!product.itemDiscounts) {
      product.itemDiscounts = {
        trnDiscountsPortion: 0,
        trnPromotionsPortion: 0,
        refundPortion: 0,
      };
    }
    return product;
  }

  markProduct(product: Product) {
    this.productSource.next(product);
  }
  stopPeerSyncProcess(peerSync: boolean) {
    this.peerSource.next(peerSync);
  }

  scanStart(isScanning: boolean) {
    this.scanSource.next(isScanning);
  }

  promotionRemainingCalculation(promotion: TransactionPromotion[]) {
    var promotionValue = 0;
    promotion.forEach((element) => {
      promotionValue += element.promotionAmount;
    });
    return promotionValue;
  }

  discountRemainingCalculation(discount: TransactionDiscount[]) {
    var discountValue = 0;
    discount.forEach((element) => {
      discountValue += element.discountAmount;
    });
    return discountValue;
  }

  discountRefundedCalculation(discount: TransactionDiscount[]) {
    var discountValue = 0;
    discount.forEach((element) => {
      if (element.discountAmount < 0) {
        discountValue += element.discountAmount;
      }
    });
    return discountValue;
  }

  generateNewItemLineId(products: Product[]) {
    let largestNumber: any = 0;

    if (products.length > 0) {
      let itemLineIdArray = products.map((product) => {
        return product.itemLineId;
      });

      largestNumber = itemLineIdArray.reduce((a :any, b:any) => Math.max(a, b));
    }

    return largestNumber + 1;
  }

  updateBasketProductDataAsPerQuantity(
    product: any,
    quantity: number
  ): Product {

    AppLoggerService.logDebug(this.className, "updateBasketProductDataAsPerQuantity", `ebasket: updateBasketProductDataAsPerQuantity : ${quantity} product: ${JSON.stringify(product)}`);
    product.quantity = quantity;
    if (product.hasOwnProperty("lineDiscount")) {
      // && this.product.quantity !== this.productDetailForm.controls.quantity.value
      product.lineDiscount.discountedLinePrice = Number(
        this.numberUtilService.getFlooredFixed(
          product.lineDiscount.unitPrice * quantity,
          2
        )
      );
      product.lineDiscount.discountAmount =
        product.totalLinePrice - product.lineDiscount.discountedLinePrice;
    } else {
      product.totalLinePrice =
        quantity * this.numberUtilService.trimTo2Decimal(product.selectedPrice);
    }
    if (product.lineDiscount) {
      product.total = product.lineDiscount.discountedLinePrice;
    } else {
      product.total = product.totalLinePrice;
    }
    AppLoggerService.logDebug(this.className, "updateBasketProductDataAsPerQuantity", `ebasket: updateBasketProductDataAsPerQuantity : ${quantity} product: ${JSON.stringify(product)}`);

    return product;
  }

  filterProductListWithWebOrder(isWebOrder: boolean): Product[] {
    AppLoggerService.logDebug(this.className, "filterProductListWithWebOrder", "filterProductListWithWebOrder hit", this.currentBasket.products);
    if (isWebOrder)
      return this.currentBasket.products.filter((x) => x.isWebOrder);
    else {
      return this.currentBasket.products.filter((x) => !x.isWebOrder);
    }
  }

  filterProductListWithDelivery(): Product[] {
    AppLoggerService.logDebug(this.className, "filterProductListWithDelivery", "filterProductListWithDelivery hit", this.currentBasket.products);
    return this.currentBasket.products.filter((x) => x.deliveryMethod === DELIVERY_METHODS.DELIVERY.toString());
  }

  filterProductListWithDeliveryGroup(group: string, products: Product[]): Product[] {
    AppLoggerService.logDebug(this.className, "filterProductListWithDeliveryGroup", "filterProductListWithDeliveryGroup hit", products);
    return products.filter((x) => x.deliveryGroup === group);
  }

  generateNewDeliveryGroupNumber(products: Product[]) {
    let largestNumber = 0;

    if (products.length > 0) {
      let deliveryGroupArr = products.map((product) => {
        if (product.deliveryGroup) {
          return parseInt(product.deliveryGroup);
        }
        return 0;
      });

      largestNumber = deliveryGroupArr.reduce((a, b) => Math.max(a, b));
    }

    return (largestNumber + 1).toString();
  }

  getDeliveryGroupNumbers(products: Product[]) {
    AppLoggerService.logDebug(this.className, "getDeliveryGroupNumbers", "getDeliveryGroupNumbers initiating : ",products);

    let uniqueAndsortedResult: number[] = [];

    if (products.length > 0) {
      let deliveryGroupArr = products.map((product) => {
        if (product.deliveryGroup) {
          return parseInt(product.deliveryGroup);
        }
        else if (product.deliveryMethod === "1") {
          return 1;
        }
        return 0;
      });

      uniqueAndsortedResult = Array.from(new Set(deliveryGroupArr)).sort((a, b) => a - b);
      AppLoggerService.logDebug(this.className, "getDeliveryGroupNumbers", "getDeliveryGroupNumbers : uniqueAndsortedResult", uniqueAndsortedResult);
    }
    return uniqueAndsortedResult;
  }

  updateProductsWithNewDeliveryGroupNumber(products: Product[]) {
    AppLoggerService.logDebug(this.className, "updateProductsWithNewDeliveryGroupNumber", "initiating : ", products);

    let newGroupNumber = this.generateNewDeliveryGroupNumber(this.currentBasket.products);
    if (products.length > 0) {
      this.currentBasket.products.forEach(cBProducts => {
        products.forEach(element => {
          if (cBProducts.itemLineId === element.itemLineId) {
            cBProducts.deliveryGroup = newGroupNumber;
          }

        });
      });

    }

    this.updateBasket(this.currentBasket);
    return;
  }

  calculateEstimatedTime(products: any[]) {
    try {
      let time;
      let estmatedTime: number = 0;
      let estimatedDays: string = "Hours";
      products.forEach((element: { leadTime: { type: string; value: any; }; }) => {

        if (element.leadTime.type.toLocaleLowerCase() === "weeks") {
          time = Number(element.leadTime.value) * 24 * 7;
          estmatedTime = Math.max(estmatedTime, time);
          estimatedDays = (estmatedTime === time) ? element.leadTime.type : estimatedDays
          AppLoggerService.logDebug(this.className, "calculateEstimatedTime", `week ${estmatedTime} ${estimatedDays}`);

        } else if (element.leadTime.type.toLocaleLowerCase() === "days") {
          time = Number(element.leadTime.value) * 24;
          estmatedTime = Math.max(estmatedTime, time);
          estimatedDays = (estmatedTime === time) ? element.leadTime.type : estimatedDays

          AppLoggerService.logDebug(this.className, "calculateEstimatedTime", `days ${estmatedTime} ${estimatedDays}`);

        }
        else if (element.leadTime.type.toLocaleLowerCase() === "months") {
          time = Number(element.leadTime.value) * 24 * 30;
          estmatedTime = Math.max(estmatedTime, time);
          estimatedDays = (estmatedTime === time) ? element.leadTime.type : estimatedDays

          AppLoggerService.logDebug(this.className, "calculateEstimatedTime", `months ${estmatedTime}  ${estimatedDays}`);

        }
        else if (element.leadTime.type.toLocaleLowerCase() === "years") {
          time = Number(element.leadTime.value) * 24 * 365;
          estmatedTime = Math.max(estmatedTime, time);
          estimatedDays = (estmatedTime === time) ? element.leadTime.type : estimatedDays

          AppLoggerService.logDebug(this.className, "calculateEstimatedTime", `years ${estmatedTime}  ${estimatedDays}`);

        } else {
          time = Number(element.leadTime.value);
          estmatedTime = Math.max(estmatedTime, time);
          estimatedDays = (estmatedTime === time) ? element.leadTime.type : estimatedDays;
          AppLoggerService.logDebug(this.className, "calculateEstimatedTime", `hours  ${estmatedTime}  ${estimatedDays}`);

        }
      });

      if (estimatedDays.toLocaleLowerCase() === "days") {
        estmatedTime = estmatedTime / 24;
      } else if (estimatedDays.toLocaleLowerCase() === "weeks") {
        estmatedTime = estmatedTime / (24 * 7);
      } else if (estimatedDays.toLocaleLowerCase() === "months") {
        estmatedTime = estmatedTime / (24 * 30);
      } else if (estimatedDays.toLocaleLowerCase() === "years") {
        estmatedTime = estmatedTime / (24 * 365);
      }

      let estimate = {
        value: estmatedTime.toString(),
        type: estimatedDays
      }
      return estimate;
    } catch (error) {
      AppLoggerService.logError(this.className, "calculateEstimatedTime", "error in calculateEstimatedTime", error);
    }
    return 0;
  }

  updateProductsWithDeliveryGroupNumber(oldGroup: string, newGroup: string) {
    AppLoggerService.logDebug(this.className, "updateProductsWithDeliveryGroupNumber initiating");

    this.currentBasket.products.forEach(cBProducts => {
      if (cBProducts.deliveryGroup === oldGroup) {
        cBProducts.deliveryGroup = newGroup;
      }

    });

    this.updateBasket(this.currentBasket);
    return;
  }

  getDeliveryInfo(): any {
    AppLoggerService.logDebug(this.className, "getDeliveryInfo", "getDeliveryGroupInfor initiating");
    try {
      let groupInfo: DeliveryInfo = {
        groups: []
      }

      this.getDeliveryGroupNumbers(this.currentBasket.products).forEach((groupNumber) => {
        let group: any = {
          groupNumber: groupNumber,
          items: [],
          estimate: {
            type: "Hours",
            value: "0"
          }
        }
        this.currentBasket.products.forEach(cBProducts => {
          if (cBProducts.deliveryGroup === groupNumber.toString()) {
            group.items.push(cBProducts);
          }
        });

        group.estimate = this.calculateEstimatedTime(group.items);

        groupInfo.groups.push(group);

      });

      return groupInfo;
    }
    catch (error) {
      AppLoggerService.logError(this.className, "getDeliveryInfo", "error while getting delivery info", error);
    }
  }
}
